import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Construction as ConstructionIcon,
  Security as SecurityIcon,
  LocalShipping as ShippingIcon,
  Support as SupportIcon,
  TrendingUp as GrowthIcon,
  People as TeamIcon,
  EmojiEvents as AwardIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';

const About: React.FC = () => {
  const values = [
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Qualité',
      description: 'Nous nous engageons à fournir des matériaux de construction de la plus haute qualité, certifiés et testés selon les normes internationales.',
    },
    {
      icon: <SupportIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Service Client',
      description: 'Notre équipe dédiée est disponible 24/7 pour vous accompagner dans tous vos projets de construction avec expertise et professionnalisme.',
    },
    {
      icon: <ShippingIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Livraison Rapide',
      description: 'Livraison le même jour pour les commandes passées avant 14h, garantissant la ponctualité de vos chantiers.',
    },
    {
      icon: <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Prix Compétitifs',
      description: 'Nous offrons les meilleurs prix du marché grâce à nos partenariats directs avec les fabricants et notre volume d\'achat.',
    },
  ];

  const achievements = [
    { number: '500+', label: 'Projets Réalisés' },
    { number: '1000+', label: 'Clients Satisfaits' },
    { number: '50+', label: 'Partenaires' },
    { number: '5', label: 'Années d\'Expérience' },
  ];

  const team = [
    {
      name: 'Mamadou Diallo',
      position: 'Directeur Général',
      avatar: 'MD',
      description: 'Expert en construction avec plus de 15 ans d\'expérience dans le secteur.',
    },
    {
      name: 'Fatou Sall',
      position: 'Directrice Commerciale',
      avatar: 'FS',
      description: 'Spécialiste en relations clients et développement commercial.',
    },
    {
      name: 'Ousmane Ba',
      position: 'Responsable Technique',
      avatar: 'OB',
      description: 'Ingénieur civil avec expertise en matériaux de construction.',
    },
    {
      name: 'Aissatou Diop',
      position: 'Responsable Logistique',
      avatar: 'AD',
      description: 'Experte en gestion de la chaîne d\'approvisionnement.',
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Hero Section */}
      <Box sx={{ textAlign: 'center', mb: 8 }}>
        <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          À Propos de TotalBat
        </Typography>
        <Typography variant="h5" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
          Votre partenaire de confiance pour tous vos besoins de construction au Sénégal
        </Typography>
      </Box>

      {/* Mission & Vision */}
      <Grid container spacing={6} sx={{ mb: 8 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 4, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <ConstructionIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
              <Typography variant="h4" component="h2">
                Notre Mission
              </Typography>
            </Box>
            <Typography variant="body1" paragraph>
              Fournir des matériaux de construction de qualité supérieure, accompagnés d'un service client exceptionnel, 
              pour contribuer au développement durable du secteur de la construction au Sénégal.
            </Typography>
            <Typography variant="body1">
              Nous nous engageons à être le partenaire de choix des professionnels et particuliers 
              en offrant des solutions complètes et innovantes.
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 4, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <GrowthIcon sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
              <Typography variant="h4" component="h2">
                Notre Vision
              </Typography>
            </Box>
            <Typography variant="body1" paragraph>
              Devenir le leader du marché des matériaux de construction au Sénégal, 
              reconnu pour notre excellence, notre innovation et notre contribution 
              au développement économique du pays.
            </Typography>
            <Typography variant="body1">
              Nous aspirons à être un acteur majeur dans la transformation 
              du secteur de la construction en Afrique de l'Ouest.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Values */}
      <Box sx={{ mb: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom>
          Nos Valeurs
        </Typography>
        <Typography variant="h6" color="text.secondary" align="center" sx={{ mb: 6 }}>
          Les principes qui guident nos actions au quotidien
        </Typography>
        <Grid container spacing={4}>
          {values.map((value, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ height: '100%', textAlign: 'center' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ mb: 2 }}>
                    {value.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {value.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {value.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Achievements */}
      <Box sx={{ mb: 8 }}>
        <Paper sx={{ p: 6, bgcolor: 'primary.main', color: 'white' }}>
          <Typography variant="h3" component="h2" align="center" gutterBottom sx={{ color: 'white' }}>
            Nos Réalisations
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {achievements.map((achievement, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h2" component="div" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                    {achievement.number}
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'white' }}>
                    {achievement.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Box>

      {/* Team */}
      <Box sx={{ mb: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom>
          Notre Équipe
        </Typography>
        <Typography variant="h6" color="text.secondary" align="center" sx={{ mb: 6 }}>
          Des experts passionnés à votre service
        </Typography>
        <Grid container spacing={4}>
          {team.map((member, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ height: '100%', textAlign: 'center' }}>
                <CardContent sx={{ p: 3 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      mx: 'auto',
                      mb: 2,
                      bgcolor: 'primary.main',
                      fontSize: '1.5rem',
                      color: 'white',
                    }}
                  >
                    {member.avatar}
                  </Avatar>
                  <Typography variant="h6" gutterBottom>
                    {member.name}
                  </Typography>
                  <Chip
                    label={member.position}
                    color="primary"
                    size="small"
                    sx={{ 
                      mb: 2,
                      color: 'white',
                      '& .MuiChip-label': {
                        color: 'white',
                      }
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {member.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Why Choose Us */}
      <Box>
        <Paper sx={{ p: 6, bgcolor: 'background.default' }}>
          <Typography variant="h3" component="h2" align="center" gutterBottom>
            Pourquoi Choisir TotalBat ?
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <AwardIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Qualité Certifiée
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tous nos produits sont certifiés selon les normes internationales 
                  et testés pour garantir leur performance.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <TeamIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Expertise Technique
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Notre équipe d'experts vous accompagne dans le choix des matériaux 
                  adaptés à votre projet.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <ShippingIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Service Premium
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Livraison rapide, support 24/7 et garantie satisfaction 
                  pour une expérience client exceptionnelle.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Container>
  );
};

export default About; 